import {RemoteConstantsModel} from "../models/remoteConstants.model";
import {AttributeManifest, LocalizationKeyManifestEntry} from "../models/attributeManifest.model";
import {namespacedTypeDescriptionKey} from "./descriptionKeys";

// "Used" here means referenced from RemoteConstants data (entity ids, Metadata
// uids/ids, image/sound/imagePack keys, etc.) or named by Client code: the
// attribute manifest's LocalizationKeys (the `loc.` convention).

// The prefix every Client-owned key carries.
export const CODE_KEY_PREFIX = 'loc.';

// Display marker for undefined localization keys. getLocalizationValue
// returns `[[key]]` instead of the bare key so every surface that renders a
// localized value makes the missing entry obvious to the user. Use
// isMissingLocalization to branch on it instead of comparing against the key.
export function missingLocalization(key: string): string {
    return `[[${key}]]`;
}

export function isMissingLocalization(value: string): boolean {
    return value.startsWith('[[') && value.endsWith(']]');
}

// The Client-owned keys by key, or undefined for a manifest exported before
// LocalizationKeys existed (or none loaded yet) — callers then fall back to the
// prefix alone.
export function codeLocalizationKeys(manifest: AttributeManifest | undefined): Map<string, LocalizationKeyManifestEntry> | undefined {
    if (!manifest?.LocalizationKeys) return undefined;
    return new Map(manifest.LocalizationKeys.map(entry => [entry.Key, entry]));
}

// The branch's key list: the English column when there is one, else the first
// language — the same column the Localization tab lists.
function branchKeys(state: RemoteConstantsModel): Set<string> {
    const localization = state.localization ?? {};
    const language = localization['en'] ?? Object.values(localization)[0] ?? {};
    return new Set(Object.keys(language));
}

// Client-owned keys this branch has no entry for, sorted by key. In game such a key
// falls back to Master's text, and past that to the Client's own English where the
// call site has one, else to the raw key. Empty for a manifest without the section.
export function missingCodeLocalization(state: RemoteConstantsModel, manifest: AttributeManifest | undefined): LocalizationKeyManifestEntry[] {
    const present = branchKeys(state);
    return (manifest?.LocalizationKeys ?? []).filter(entry => !present.has(entry.Key));
}

// `loc.` keys this branch holds that the Client no longer names. Unused, but a build
// still in players' hands may ask for them, so pruning them is a judgement call the
// prune dialog flags. Empty for a manifest without the section.
export function staleCodeLocalizationKeys(state: RemoteConstantsModel, manifest: AttributeManifest | undefined): string[] {
    const code = codeLocalizationKeys(manifest);
    if (!code) return [];
    return Array.from(branchKeys(state)).filter(key => key.startsWith(CODE_KEY_PREFIX) && !code.has(key)).sort();
}

export function getUsedLocalizationKeys(state: RemoteConstantsModel, manifest?: AttributeManifest): Set<string> {
    const keys = new Set<string>();

    // Client-owned keys: the manifest's list when it carries one; for an older
    // manifest, every `loc.` key the branch holds, since nothing says which are dead.
    const code = codeLocalizationKeys(manifest);
    if (code) {
        code.forEach((_, key) => keys.add(key));
    } else {
        for (const lang of Object.values(state.localization ?? {})) {
            for (const key of Object.keys(lang)) {
                if (key.startsWith(CODE_KEY_PREFIX)) keys.add(key);
            }
        }
    }

    // Special constants
    if (state.startingUnit) keys.add(state.startingUnit);
    if (state.startingBuilding) keys.add(state.startingBuilding);

    // Units. Entity description keys (`<id>.description`) are real lookups:
    // the Client builds them for info popups (Building.cs, UnlockablePopup)
    // and the editor's entity views edit them — same pattern for buildings,
    // tiles, and resources below.
    if (state.units) {
        for (const unit of state.units) {
            keys.add(unit.id);
            keys.add(`${unit.id}.description`);
            if (unit.attributes) {
                for (const attr of unit.attributes) keys.add(attr);
            }
        }
    }

    // Buildings
    if (state.buildings) {
        for (const building of state.buildings) {
            keys.add(building.id);
            keys.add(`${building.id}.description`);
            if (building.attributes) {
                for (const attr of building.attributes) keys.add(attr);
            }
            if (building.produceableUnits) {
                for (const uid of building.produceableUnits) keys.add(uid);
            }
            if (building.rewards) {
                for (const level of building.rewards) {
                    if (level) {
                        for (const reward of level) keys.add(reward);
                    }
                }
            }
        }
    }

    // Tiles
    if (state.tiles) {
        for (const tile of state.tiles) {
            keys.add(tile.id);
            keys.add(`${tile.id}.description`);
            if (tile.supportingTile) keys.add(tile.supportingTile);
            if (tile.tileType) keys.add(tile.tileType);
            if (tile.resources) {
                for (const r of tile.resources) keys.add(r);
            }
            if (tile.buildings) {
                for (const b of tile.buildings) keys.add(b);
            }
        }
    }

    // Resources
    if (state.resources) {
        for (const resource of state.resources) {
            keys.add(resource.id);
            keys.add(`${resource.id}.description`);
            if (resource.attributes) {
                for (const attr of resource.attributes) keys.add(attr);
            }
        }
    }

    // Tech tree
    if (state.techTree) {
        for (const node of state.techTree) {
            keys.add(node.id);
            keys.add(`${node.id}.description`);
            if (node.unlocks) {
                for (const unlock of node.unlocks) keys.add(unlock);
            }
            if (node.children) {
                for (const child of node.children) keys.add(child);
            }
        }
    }

    // All metadata collections: uid as instance name, uid.description as the
    // per-instance description, id.description as the shared fallback.
    const metadataCollections = [
        state.unitAttributes,
        state.buildingAttributes,
        state.techActionAttributes,
        state.techPassiveAttributes,
        state.rewards,
    ];
    const descriptionIds = new Set<string>();
    for (const collection of metadataCollections) {
        if (!collection) continue;
        for (const meta of collection) {
            if (meta.uid) {
                keys.add(meta.uid);
                keys.add(`${meta.uid}.description`);
            }
            if (meta.id) {
                descriptionIds.add(meta.id);                                // legacy bare key (transitional)
                // Namespaced key needs the uid's first segment — skip it for
                // legacy records that predate uids (e.g. older branches).
                if (meta.uid) keys.add(namespacedTypeDescriptionKey(meta.uid, meta.id));  // new namespaced key
            }
        }
    }
    // Legacy <type>.description keys stay "expected" through the migration so
    // they aren't flagged as orphans before the purge step removes them.
    descriptionIds.forEach(id => keys.add(`${id}.description`));

    // Asset keys
    if (state.images) {
        for (const key of Object.keys(state.images)) keys.add(key);
    }
    if (state.sounds) {
        for (const key of Object.keys(state.sounds)) keys.add(key);
    }
    if (state.imagePacks) {
        for (const key of Object.keys(state.imagePacks)) keys.add(key);
    }

    return keys;
}
